<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  addVocabularyBankWord,
  explainPromptWord,
  generateSpeechAudio,
  listVocabularyBanks,
  userPrompt,
} from '@/services/backendService.js'
import AiChatMessage from './components/AiChatMessage/AiChatMessage.vue'
import HumanChatMessage from './components/HumanChatMessage.vue'
import { useCurrentUser } from '@/composables/useCurrentUser.js'
import { MessageUsageKind } from '@/models/messageUsage.js'

const INITIAL_PROMPT =
  "Start the conversation with me. Ask me a question related to the vocabulary I'm currently learning"
const MAX_AUDIO_MESSAGES = 5
const prompt = ref('')
const isLoading = ref(false)
const messages = ref([])
const learningLanguage = ref('en')
const explanationLanguage = ref('pl')
const selectedKnownWordsBankId = ref('')
const selectedKnownKanjisBankId = ref('')
const selectedFocusWordsBankId = ref('')
const vocabularyBanks = ref([])
const isLoadingVocabularyBanks = ref(false)
const vocabularyBanksError = ref('')
const messagesContainer = ref(null)
const promptInput = ref(null)
const confirmingKnownWords = ref(new Set())
const savingKnownWords = ref(new Set())
const addKnownWordError = ref('')
const generateAudio = ref(false)
const hasSessionStarted = ref(false)
const { currentUser, refreshCurrentUser } = useCurrentUser()
let messageIdCounter = 0

const createMessageId = () => {
  messageIdCounter += 1
  return `message-${messageIdCounter}`
}

watch(learningLanguage, () => {
  selectedKnownWordsBankId.value = ''
  selectedKnownKanjisBankId.value = ''
  selectedFocusWordsBankId.value = ''
  hasSessionStarted.value = false
  clearAudioState()
  messages.value = []
  prompt.value = ''
})

const languageCodeByValue = {
  en: 'en',
  english: 'en',
  es: 'es',
  spanish: 'es',
  ru: 'ru',
  russian: 'ru',
  ja: 'ja',
  japanese: 'ja',
}

const normalizeLanguageCode = (language) => {
  const normalized = String(language ?? '')
    .trim()
    .toLowerCase()
  return languageCodeByValue[normalized] ?? normalized
}

const filteredVocabularyBanks = computed(() => {
  const selectedLanguageCode = normalizeLanguageCode(learningLanguage.value)
  return vocabularyBanks.value.filter(
    (bank) => normalizeLanguageCode(bank.language) === selectedLanguageCode,
  )
})
const isJapaneseLearningLanguage = computed(
  () => normalizeLanguageCode(learningLanguage.value) === 'ja',
)
const knownWordsBankOptions = computed(() =>
  filteredVocabularyBanks.value.filter((bank) => bank.scope === 'known'),
)
const knownKanjisBankOptions = computed(() =>
  filteredVocabularyBanks.value.filter((bank) => bank.scope === 'kanji'),
)
const focusWordsBankOptions = computed(() =>
  filteredVocabularyBanks.value.filter((bank) => bank.scope === 'focus'),
)

const hasKnownWordsSource = computed(() => Boolean(selectedKnownWordsBankId.value))
const hasFocusWordsSource = computed(() => Boolean(selectedFocusWordsBankId.value))
const canAddKnownWords = computed(() => Boolean(selectedKnownWordsBankId.value))

const chatConfig = computed(() => ({
  knownWordsBankId: selectedKnownWordsBankId.value,
  knownKanjisBankId: selectedKnownKanjisBankId.value,
  focusWordsBankId: selectedFocusWordsBankId.value,
  learningLanguage: learningLanguage.value.trim(),
  explanationLanguage: explanationLanguage.value.trim(),
}))
const isSessionConfigured = computed(() => {
  return hasKnownWordsSource.value && hasFocusWordsSource.value
})
const textMessageUsage = computed(
  () => currentUser.value?.messageUsage?.[MessageUsageKind.TEXT] ?? null,
)
const audioTtsMessageUsage = computed(
  () => currentUser.value?.messageUsage?.[MessageUsageKind.AUDIO_TTS] ?? null,
)
const isTextMessageQuotaExhausted = computed(() => {
  const usage = textMessageUsage.value
  if (!usage) return false

  return usage.usedMessages >= usage.currentMessageLimit
})
const isAudioTtsMessageQuotaExhausted = computed(() => {
  const usage = audioTtsMessageUsage.value
  if (!usage) return false

  return usage.usedMessages >= usage.currentMessageLimit
})
const audioToggleLabel = computed(() => {
  if (isAudioTtsMessageQuotaExhausted.value) return 'No audio quota'

  return 'Generate'
})
watch(isAudioTtsMessageQuotaExhausted, (isExhausted) => {
  if (isExhausted) {
    generateAudio.value = false
  }
})
const isComposerDisabled = computed(
  () => isLoading.value || !hasSessionStarted.value || isTextMessageQuotaExhausted.value,
)
const sendButtonLabel = computed(() => {
  if (isTextMessageQuotaExhausted.value) return 'No more quota this week'
  if (!hasSessionStarted.value) return 'Start session first'
  if (isLoading.value) return 'Sending...'

  return 'Send'
})
const canStartSession = computed(() => {
  return (
    isSessionConfigured.value &&
    !hasSessionStarted.value &&
    !isLoading.value &&
    !isTextMessageQuotaExhausted.value
  )
})
const startButtonLabel = computed(() => {
  if (isTextMessageQuotaExhausted.value) return 'No more quota this week'
  if (isLoading.value) return 'Starting...'

  return 'Start'
})
const setupInfoMessage = computed(() => {
  if (hasSessionStarted.value) return ''
  if (!hasKnownWordsSource.value) {
    return 'Select languages and a known words bank to prepare the session.'
  }
  if (!hasFocusWordsSource.value) {
    return 'Select a focus words bank to prepare the session.'
  }
  return 'Click Start to begin the conversation.'
})

const selectedKnownWordsBank = computed(() =>
  vocabularyBanks.value.find(
    (bank) => String(bank.dbId) === String(selectedKnownWordsBankId.value),
  ),
)

const buildConversationHistory = () =>
  messages.value.map((message) => ({
    role: message.type === 'bot' ? 'assistant' : 'user',
    content: message.content,
  }))

const findMessageById = (messageId) => messages.value.find((message) => message.id === messageId)

const updateMessageById = (messageId, patch) => {
  messages.value = messages.value.map((message) =>
    message.id === messageId ? { ...message, ...patch } : message,
  )
}

const updateMessageStatsById = (messageId, statsPatch) => {
  const message = findMessageById(messageId)
  if (!message) return

  updateMessageById(messageId, {
    stats: {
      ...(message.stats ?? {}),
      ...statsPatch,
    },
  })
}

const revokeMessageAudio = (message) => {
  if (!message.audioUrl) return

  URL.revokeObjectURL(message.audioUrl)
  message.audioUrl = ''
  message.audioSource = null
}

const clearAudioState = () => {
  messages.value.forEach((message) => revokeMessageAudio(message))
}

const pruneAudioUrls = () => {
  const audioMessages = messages.value.filter((message) => message.audioUrl)
  const staleAudioMessages = audioMessages.slice(
    0,
    Math.max(0, audioMessages.length - MAX_AUDIO_MESSAGES),
  )

  staleAudioMessages.forEach((message) => revokeMessageAudio(message))
}

const requestBotAudio = async (messageId, text) => {
  if (isAudioTtsMessageQuotaExhausted.value) {
    updateMessageById(messageId, {
      audioError: 'No audio quota this week',
      isAudioLoading: false,
    })
    return
  }

  updateMessageById(messageId, {
    isAudioLoading: true,
    audioError: '',
  })

  try {
    const audio = await generateSpeechAudio(text, learningLanguage.value)
    const message = findMessageById(messageId)
    if (!message) return

    revokeMessageAudio(message)
    const audioUrl = URL.createObjectURL(audio.blob)
    updateMessageById(messageId, {
      audioUrl,
      audioSource: {
        src: audioUrl,
        type: audio.contentType,
      },
      audioContentType: audio.contentType,
      audioOutputFormat: audio.outputFormat,
      isAudioLoading: false,
    })
    pruneAudioUrls()
  } catch (error) {
    console.error('Failed to generate speech audio:', error)
    updateMessageById(messageId, {
      audioError:
        error?.response?.status === 403 ? 'No audio quota this week' : 'Audio unavailable',
      isAudioLoading: false,
    })
  } finally {
    await refreshMessageUsage()
    updateMessageById(messageId, {
      isAudioLoading: false,
    })
  }
}

const buildBotMessage = (overrides = {}) => ({
  id: createMessageId(),
  type: 'bot',
  content: '',
  markedContent: '',
  stats: null,
  audioUrl: '',
  audioSource: null,
  audioError: '',
  isAudioLoading: false,
  isResponseLoading: false,
  ...overrides,
})

const startSession = async () => {
  if (!canStartSession.value) return

  hasSessionStarted.value = true
  await sendPrompt(INITIAL_PROMPT, { showUserMessage: false })
  await nextTick()
  promptInput.value?.focus()
}

const scrollLatestBotMessageIntoView = async () => {
  await nextTick()

  const container = messagesContainer.value
  const botMessages = container?.querySelectorAll('.message.bot')
  const latestBotMessage = botMessages?.[botMessages.length - 1]
  if (!container || !latestBotMessage) return

  container.scrollTop = latestBotMessage.offsetTop - container.offsetTop
}

const scrollMessagesToBottom = async () => {
  await nextTick()

  const container = messagesContainer.value
  if (!container) return

  container.scrollTop = container.scrollHeight
}

const appendBotMessageContent = (messageId, delta) => {
  const message = findMessageById(messageId)
  if (!message) return

  updateMessageById(messageId, {
    content: `${message.content}${delta}`,
    markedContent: '',
  })
  void scrollMessagesToBottom()
}

const refreshMessageUsage = async () => {
  try {
    await refreshCurrentUser()
  } catch (error) {
    console.error('Failed to refresh message usage:', error)
  }
}

const uniqueWords = (words) => {
  const seenWords = new Set()
  return words.filter((word) => {
    const normalizedWord = normalizeWord(String(word ?? ''))
    if (!normalizedWord || seenWords.has(normalizedWord)) return false

    seenWords.add(normalizedWord)
    return true
  })
}

const buildExplanationStatusMap = (unknownWords) =>
  Object.fromEntries(unknownWords.map((word) => [word, { isLoading: true, error: '' }]))

const buildExplanationMap = (unknownWords, existingExplanations = {}) =>
  Object.fromEntries(unknownWords.map((word) => [word, existingExplanations[word] ?? '']))

const preparePendingExplanations = (messageId, stats) => {
  const unknownWords = uniqueWords(stats?.unknown ?? [])
  if (!unknownWords.length) return []

  updateMessageStatsById(messageId, {
    unknown: unknownWords,
    explanations: buildExplanationMap(unknownWords, stats?.explanations),
    explanationStatuses: buildExplanationStatusMap(unknownWords),
  })

  return unknownWords
}

const findSentenceContainingWord = (statement, word) => {
  const normalizedWord = normalizeWord(word)
  const sentences = String(statement ?? '').match(/[^.!?。！？\n]+[.!?。！？]?/g) ?? []
  const matchingSentence = sentences.find((sentence) =>
    normalizeWord(sentence).includes(normalizedWord),
  )

  return matchingSentence?.trim() || null
}

const updateExplanationEntry = (messageId, word, { explanation, isLoading, error }) => {
  const message = findMessageById(messageId)
  const stats = message?.stats
  if (!stats) return

  const explanations = { ...(stats.explanations ?? {}) }
  if (explanation !== undefined) {
    explanations[word] = explanation
  }

  updateMessageStatsById(messageId, {
    explanations,
    explanationStatuses: {
      ...(stats.explanationStatuses ?? {}),
      [word]: {
        ...(stats.explanationStatuses?.[word] ?? {}),
        ...(isLoading !== undefined ? { isLoading } : {}),
        ...(error !== undefined ? { error } : {}),
      },
    },
  })
}

const explanationErrorMessage = (error) => {
  const status = error?.response?.status
  if (status === 402 || status === 403) return 'No explanation quota this week'

  return 'Explanation unavailable'
}

const requestUnknownWordExplanation = async (messageId, word, statement, requestConfig) => {
  try {
    const response = await explainPromptWord({
      word,
      statement,
      sentence: findSentenceContainingWord(statement, word),
      learningLanguage: requestConfig.learningLanguage,
      explanationLanguage: requestConfig.explanationLanguage,
    })
    updateExplanationEntry(messageId, word, {
      explanation: response.explanation ?? '',
      isLoading: false,
      error: '',
    })
  } catch (error) {
    console.error(`Failed to explain "${word}":`, error)
    updateExplanationEntry(messageId, word, {
      isLoading: false,
      error: explanationErrorMessage(error),
    })
  } finally {
    await refreshMessageUsage()
  }
}

const requestUnknownWordExplanations = (messageId, response, requestConfig) => {
  const unknownWords = preparePendingExplanations(messageId, response.stats)
  unknownWords.forEach((word) => {
    void requestUnknownWordExplanation(messageId, word, response.answer, requestConfig)
  })
}

const sendPrompt = async (message, { showUserMessage = true } = {}) => {
  let botMessageId = ''

  try {
    isLoading.value = true
    const conversationHistory = buildConversationHistory()
    const requestChatConfig = { ...chatConfig.value }

    if (showUserMessage) {
      messages.value.push({
        id: createMessageId(),
        type: 'user',
        content: message,
      })
      await scrollMessagesToBottom()
    }

    const botMessage = buildBotMessage({ isResponseLoading: true })
    botMessageId = botMessage.id
    messages.value.push(botMessage)
    await scrollLatestBotMessageIntoView()

    const response = await userPrompt(message, conversationHistory, requestChatConfig, {
      onDelta: (delta) => appendBotMessageContent(botMessageId, delta),
    })
    updateMessageById(botMessageId, {
      content: response.answer,
      markedContent: response.markedAnswer,
      stats: response.stats,
      isResponseLoading: false,
    })
    requestUnknownWordExplanations(botMessageId, response, requestChatConfig)
    await scrollLatestBotMessageIntoView()
    if (generateAudio.value) {
      void requestBotAudio(botMessageId, response.answer)
    }
    await refreshMessageUsage()
  } catch {
    const errorMessage = {
      content: 'Error: Failed to get response from AI',
      markedContent: 'Error: Failed to get response from AI',
      stats: {
        percentUnknown: 0,
        explanations: {},
        explanationStatuses: {},
        focusWordsFound: [],
      },
      audioUrl: '',
      audioSource: null,
      audioError: '',
      isAudioLoading: false,
      isResponseLoading: false,
    }
    if (botMessageId && findMessageById(botMessageId)) {
      updateMessageById(botMessageId, errorMessage)
    } else {
      messages.value.push(buildBotMessage(errorMessage))
    }
    await scrollLatestBotMessageIntoView()
  } finally {
    isLoading.value = false
  }
}

const sendMessage = async () => {
  if (isComposerDisabled.value) return

  const message = prompt.value.trim()
  if (!message) return

  prompt.value = ''
  await nextTick()
  promptInput.value?.focus()
  await sendPrompt(message)
  await nextTick()
  promptInput.value?.focus()
}

const handlePromptKeydown = (event) => {
  if (event.key !== 'Enter' || event.shiftKey) {
    return
  }

  event.preventDefault()
  void sendMessage()
}

const normalizeWord = (word) => word.trim().toLowerCase()

const isKnownWordConfirming = (word) => confirmingKnownWords.value.has(normalizeWord(word))

const isKnownWordSaving = (word) => savingKnownWords.value.has(normalizeWord(word))

const isKnownWordSaved = (word) => {
  const normalizedWord = normalizeWord(word)
  return (
    selectedKnownWordsBank.value?.words?.some(
      (knownWord) => normalizeWord(knownWord) === normalizedWord,
    ) ?? false
  )
}

const buildUnknownWordEntries = (message) =>
  uniqueWords(message.stats?.unknown ?? Object.keys(message.stats?.explanations ?? {})).map(
    (word) => {
      const explanationStatus = message.stats?.explanationStatuses?.[word] ?? {}

      return {
        word,
        explanation: message.stats?.explanations?.[word] ?? '',
        explanationError: explanationStatus.error ?? '',
        isExplanationLoading: Boolean(explanationStatus.isLoading),
        isConfirming: isKnownWordConfirming(word),
        isSaving: isKnownWordSaving(word),
        isSaved: isKnownWordSaved(word),
      }
    },
  )

const requestAddWordConfirmation = (word) => {
  if (!selectedKnownWordsBankId.value || isKnownWordSaving(word) || isKnownWordSaved(word)) {
    return
  }

  confirmingKnownWords.value = new Set([...confirmingKnownWords.value, normalizeWord(word)])
}

const cancelAddWordConfirmation = (word) => {
  const updatedConfirmingWords = new Set(confirmingKnownWords.value)
  updatedConfirmingWords.delete(normalizeWord(word))
  confirmingKnownWords.value = updatedConfirmingWords
}

const addWordToKnownBank = async (word) => {
  if (!selectedKnownWordsBankId.value || isKnownWordSaving(word) || isKnownWordSaved(word)) {
    return
  }

  const normalizedWord = normalizeWord(word)
  cancelAddWordConfirmation(word)
  savingKnownWords.value = new Set([...savingKnownWords.value, normalizedWord])
  addKnownWordError.value = ''

  try {
    const updatedBank = await addVocabularyBankWord(selectedKnownWordsBankId.value, word)
    vocabularyBanks.value = vocabularyBanks.value.map((bank) =>
      bank.dbId === updatedBank.dbId ? updatedBank : bank,
    )
  } catch {
    addKnownWordError.value = `Failed to add "${word}" to known words.`
  } finally {
    const updatedSavingWords = new Set(savingKnownWords.value)
    updatedSavingWords.delete(normalizedWord)
    savingKnownWords.value = updatedSavingWords
  }
}

onMounted(async () => {
  await refreshMessageUsage()

  try {
    isLoadingVocabularyBanks.value = true
    vocabularyBanks.value = await listVocabularyBanks()
  } catch {
    vocabularyBanksError.value = 'Failed to load vocabulary banks'
  } finally {
    isLoadingVocabularyBanks.value = false
  }
})

onBeforeUnmount(() => {
  clearAudioState()
})
</script>

<template>
  <div class="main-content">
    <div class="layout">
      <section class="session-setup" aria-labelledby="session-setup-title">
        <div class="session-setup-header">
          <h1 id="session-setup-title">Session Setup</h1>
        </div>

        <div class="session-fields">
          <label class="field">
            <span>Language you learn</span>
            <select v-model="learningLanguage">
              <option value="en">🇬🇧 English</option>
              <option value="es">🇪🇸 Spanish</option>
              <option value="ru">🇷🇺 Russian</option>
              <option value="ja">🇯🇵 Japanese</option>
            </select>
          </label>

          <label class="field">
            <span>Explanation language</span>
            <select v-model="explanationLanguage">
              <option value="pl">🇵🇱 Polish</option>
              <option value="en">🇬🇧 English</option>
            </select>
          </label>

          <label class="field">
            <span>Known words bank</span>
            <select v-model.number="selectedKnownWordsBankId" :disabled="isLoadingVocabularyBanks">
              <option value="">Select a bank</option>
              <option v-for="bank in knownWordsBankOptions" :key="bank.dbId" :value="bank.dbId">
                {{ bank.title }} ({{ bank.wordCount }} words)
              </option>
            </select>
          </label>

          <label v-if="isJapaneseLearningLanguage" class="field">
            <span>Known kanji bank</span>
            <select v-model.number="selectedKnownKanjisBankId" :disabled="isLoadingVocabularyBanks">
              <option value="">Select a bank</option>
              <option v-for="bank in knownKanjisBankOptions" :key="bank.dbId" :value="bank.dbId">
                {{ bank.title }} ({{ bank.wordCount }} items)
              </option>
            </select>
          </label>

          <label class="field">
            <span>Focus words bank</span>
            <select v-model.number="selectedFocusWordsBankId" :disabled="isLoadingVocabularyBanks">
              <option value="">Select a bank</option>
              <option v-for="bank in focusWordsBankOptions" :key="bank.dbId" :value="bank.dbId">
                {{ bank.title }} ({{ bank.wordCount }} words)
              </option>
            </select>
          </label>

          <label class="field audio-field">
            <span>Audio replies</span>
            <span class="checkbox-control">
              <input
                v-model="generateAudio"
                type="checkbox"
                :disabled="isAudioTtsMessageQuotaExhausted"
              />
              <span>{{ audioToggleLabel }}</span>
            </span>
          </label>

          <div v-if="!hasSessionStarted" class="field session-start-field">
            <span>&nbsp;</span>
            <button @click="startSession" class="start-button" :disabled="!canStartSession">
              {{ startButtonLabel }}
            </button>
          </div>
        </div>

        <div v-if="vocabularyBanksError" class="summary-card error-card">
          {{ vocabularyBanksError }}
        </div>
      </section>

      <div class="chat">
        <h1>Chat</h1>
        <div class="chat-container">
          <div ref="messagesContainer" class="messages">
            <div v-if="!hasSessionStarted" class="setup-info-bar">
              {{ setupInfoMessage }}
            </div>
            <template v-for="(message, index) in messages" :key="message.id || index">
              <AiChatMessage
                v-if="message.type === 'bot'"
                :content="message.content"
                :marked-content="message.markedContent"
                :stats="message.stats"
                :unknown-word-entries="buildUnknownWordEntries(message)"
                :can-add-known-words="canAddKnownWords"
                :unknown-word-error="addKnownWordError"
                :audio-source="message.audioSource"
                :audio-url="message.audioUrl"
                :audio-error="message.audioError"
                :is-audio-loading="message.isAudioLoading"
                :is-response-loading="message.isResponseLoading"
                audio-label="Bot response audio"
                @request-add-known-word="requestAddWordConfirmation"
                @confirm-add-known-word="addWordToKnownBank"
                @cancel-add-known-word="cancelAddWordConfirmation"
              />
              <HumanChatMessage v-else :content="message.content" />
            </template>
          </div>
          <div class="composer">
            <textarea
              ref="promptInput"
              v-model="prompt"
              placeholder="Enter your message..."
              class="chat-input"
              :disabled="isComposerDisabled"
              @keydown="handlePromptKeydown"
            ></textarea>
            <button @click="sendMessage" class="send-button" :disabled="isComposerDisabled">
              {{ sendButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.main-content {
  padding: 2rem 1.5rem 3rem;
}

.layout {
  display: grid;
  gap: 1.5rem;
}

.chat {
  padding: 0;
}

.chat h1 {
  color: #f6f7f2;
  font-size: clamp(2rem, 4vw, 3rem);
  margin: 0 0 16px;
}

.session-setup {
  display: grid;
  gap: 1rem;
  border-top: 1px solid rgba(122, 203, 138, 0.18);
  border-bottom: 1px solid rgba(122, 203, 138, 0.18);
  padding: 1rem 0;
}

.session-setup-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.session-setup h1 {
  margin: 0;
  color: #f6f7f2;
  font-size: 1.15rem;
}

.session-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 0.75rem;
}

.chat-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.messages {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 400px;
  overflow-y: auto;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
}

.setup-info-bar {
  align-self: stretch;
  border: 1px solid rgba(96, 165, 250, 0.38);
  background: rgba(219, 234, 254, 0.92);
  color: #123765;
  border-radius: 8px;
  padding: 0.8rem 1rem;
  font-weight: 700;
  line-height: 1.4;
}

.composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 0.75rem;
  background: #f6f7f2;
  border: 1px solid rgba(174, 224, 182, 0.4);
  border-radius: 8px;
  padding: 0.75rem;
}

.chat-input {
  width: 100%;
  min-height: 5.5rem;
  border: 0;
  background: transparent;
  color: #101810;
  resize: vertical;
  font: inherit;
}

.chat-input:focus {
  outline: none;
}

.send-button {
  min-height: 2.6rem;
  padding: 0.65rem 1.1rem;
  background-color: #4caf50;
  color: #08120a;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 700;
}

.send-button:disabled {
  background-color: #cfd6cd;
  color: #657064;
  cursor: not-allowed;
}

.chat-response {
  padding: 15px;
  background-color: #f5f5f5;
  color: #000;
  border-radius: 4px;
  min-height: 50px;
  white-space: pre-wrap;
}

.field {
  display: grid;
  gap: 0.4rem;
  color: #aee0b6;
  font-size: 0.85rem;
  font-weight: 700;
}

.field input[type='text'],
.field select {
  width: 100%;
  min-height: 2.7rem;
  border: 1px solid rgba(174, 224, 182, 0.28);
  background: rgba(7, 14, 10, 0.62);
  color: #f6f7f2;
  border-radius: 8px;
  padding: 0.7rem 0.55rem;
  font: inherit;
}

.audio-field {
  align-content: start;
}

.session-start-field {
  align-content: end;
}

.checkbox-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.7rem;
  border: 1px solid rgba(174, 224, 182, 0.28);
  background: rgba(7, 14, 10, 0.62);
  color: #f6f7f2;
  border-radius: 8px;
  padding: 0.7rem 0.55rem;
}

.checkbox-control input {
  width: 1rem;
  height: 1rem;
  accent-color: #4caf50;
}

.start-button {
  min-height: 2.7rem;
  padding: 0.7rem 1.1rem;
  background-color: #4caf50;
  color: #08120a;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  font-weight: 700;
}

.start-button:disabled {
  background-color: #cfd6cd;
  color: #657064;
  cursor: not-allowed;
}

.field select:focus {
  border-color: #9fe2ab;
  outline: none;
}

.field option:disabled {
  font-style: italic;
}

.summary-card {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  color: #9fe2ab;
  font-size: 0.85rem;
  font-weight: 700;
}

.error-card {
  color: #ff9e9e;
}

@media (max-width: 960px) {
  .session-setup-header {
    align-items: stretch;
    flex-direction: column;
  }

  .session-fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .main-content {
    padding: 1rem 0.75rem 2rem;
  }

  .session-fields {
    grid-template-columns: 1fr;
  }

  .messages {
    padding: 0.75rem;
  }

  .composer {
    grid-template-columns: 1fr;
    padding: 0.65rem;
  }

  .send-button {
    width: 100%;
  }
}
</style>
